import React, { FC, useState } from "react";
import Stack from "react-bootstrap/Stack";
import Form from "react-bootstrap/Form";
import BSButton from "react-bootstrap/esm/Button";
import { PencilSquare } from "react-bootstrap-icons";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  useAddOrUpdateUsersCharacteristicMutation,
  useGetUsersCharacteristicListQuery,
} from "features/api/contentManagement/contentManagementApiSlice";

import { UsersCharacteristicTabTypes } from "../contentManagementTypes";

import {
  UsersCharacteristic,
  UsersCharacteristicFormControlsValues,
  usersCharacteristicsTabFields,
} from "../contentManagementUtils/tabSchema";
import AddContentForm from "./AddContentForm";
import RenderList from "./List/RenderList";
import { Button } from "./Button";

const UsersCharacteristicTab: FC<UsersCharacteristicTabTypes> = () => {
  const { t } = useTranslation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] =
    useState<UsersCharacteristic | null>(null);

  const {
    data: usersCharacteristics,
    refetch,
  }: { data: UsersCharacteristic[]; refetch: () => void } =
    useGetUsersCharacteristicListQuery();
  const [AddOrUpdateUsersCharacteristic] =
    useAddOrUpdateUsersCharacteristicMutation();

  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  const toggleUpdateForm = (initialValues: UsersCharacteristic | null) => {
    setShowUpdateForm(initialValues);
  };

  const usersCharacteristicInitialValues: UsersCharacteristic = {
    id: 0,
    name: "",
    disable: false,
  };

  const handleCreateOrUpdateUsersCharacteristic: (
    values: UsersCharacteristic
  ) => void = async (values: UsersCharacteristic) => {
    const payload = {
      id: values.id ?? 0,
      name: values.name,
      disable: values.disable,
    };

    await AddOrUpdateUsersCharacteristic(payload).unwrap();
    refetch();

    // close modal
    values.id === 0 ? toggleCreateForm() : toggleUpdateForm(null);
  };

  const handleDisable = async (usersCharacteristic: UsersCharacteristic) => {
    const payload = {
      id: usersCharacteristic.id,
      name: usersCharacteristic.name,
      disable: usersCharacteristic.disable,
    };
    await AddOrUpdateUsersCharacteristic(payload).unwrap();
  };

  const columns = [
    {
      title: t("מזהה מאפיין"),
      dataIndex: "id",
      key: "id",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 140,
    },
    {
      title: t("שם מאפיין"),
      dataIndex: "name",
      key: "name",
      flex: 1,
      renderCell: (text: string) => <span>{text}</span>,
      asc: true,
      minWidth: 140,
    },
    {
      title: t("חסום?"),
      dataIndex: "disable",
      key: "actions",
      flex: 1,
      renderCell: (usersCharacteristic: UsersCharacteristic) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <Form.Check
              type="switch"
              id="custom-switch"
              checked={usersCharacteristic.disable}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleDisable({
                  ...usersCharacteristic,
                  disable: event.target.checked,
                });
              }}
              size={20}
            />
          </Stack>
        );
      },
      asc: true,
      minWidth: 140,
    },
    {
      title: "",
      dataIndex: 4,
      key: "actions",
      flex: 1,
      renderCell: (usersCharacteristic: UsersCharacteristic) => {
        return (
          <Stack direction="horizontal" gap={0}>
            <div
              className={`button-wrapper ${
                usersCharacteristic.disable ? "disabled" : ""
              }`}
            >
              <BSButton
                key={usersCharacteristic.id}
                name={usersCharacteristic.name}
                variant="outline"
              >
                <PencilSquare
                  size={20}
                  color="primary"
                  onClick={() => {
                    toggleUpdateForm(usersCharacteristic);
                  }}
                />
              </BSButton>
            </div>
          </Stack>
        );
      },
      asc: true,
      minWidth: 100,
      maxWidth: 120,
    },
  ];

  const violationsUsersCharacteristicSchema: Yup.ObjectSchema<UsersCharacteristicFormControlsValues> =
    Yup.object().shape({
      name: Yup.string().required(t("חובה להזין שם מאפיין")),
    });

  return (
    <div className="tab-list-container">
      <div className="tab-list-header">
        <Button title={t("add")} handleClick={toggleCreateForm} />
      </div>
      <RenderList<UsersCharacteristic>
        data={usersCharacteristics ? [...usersCharacteristics] : []}
        columns={columns}
        disableField="disable"
        isDraggable
      />
      <AddContentForm
        isOpen={showCreateForm}
        toggle={toggleCreateForm}
        fields={usersCharacteristicsTabFields}
        submitForm={handleCreateOrUpdateUsersCharacteristic}
        headerTitle={t("הצעה חדשה")}
        approveBtnLabel={t("יצירת הצעה")}
        cancelBtnLabel={t("cancel")}
        initialValues={usersCharacteristicInitialValues}
        validationSchema={violationsUsersCharacteristicSchema}
      />
      <AddContentForm
        isOpen={!!showUpdateForm}
        toggle={toggleUpdateForm}
        fields={usersCharacteristicsTabFields}
        submitForm={handleCreateOrUpdateUsersCharacteristic}
        headerTitle={t("עדכון הצעה")}
        approveBtnLabel={t("עדכן הצעה")}
        cancelBtnLabel={t("cancel")}
        initialValues={showUpdateForm}
        validationSchema={violationsUsersCharacteristicSchema}
      />
    </div>
  );
};

export default UsersCharacteristicTab;
